import React, { useState, useEffect } from 'react';
import axios from 'axios';
import ServingSizeSelector from './ServingSizeSelector';
import { API_BASE_URL } from '../config';
import '../styles/mealBudgetPlanner.css';

export const MealBudgetPlanner = () => {
    const [budgetPlan, setBudgetPlan] = useState({
        duration: 'weekly',
        planType: 'single',
        budget: '',
        preferences: {
            costEffective: false,
            healthy: false
        },
        familySize: 1,
        mealsPerDay: 3,
        servingSize: 1
    });

    const [mealPlan, setMealPlan] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [ingredientPrices, setIngredientPrices] = useState({});
    const [selectedBrands, setSelectedBrands] = useState({});

    const calculateMealsNeeded = () => {
        const durationMultiplier = {
            weekly: 7,
            biweekly: 14,
            monthly: 30
        };
        const numberOfPeople = budgetPlan.planType === 'family' ? budgetPlan.familySize : 1;
        return durationMultiplier[budgetPlan.duration] * budgetPlan.mealsPerDay * numberOfPeople;
    };

    const calculateIngredientCost = (ingredient, selectedBrand) => {
        const amount = parseFloat(ingredient.amount);
        
        // Early return if no valid data
        if (!selectedBrand || !selectedBrand.cost) {
            return 0;
        }

        // Parse the cost directly from the selectedBrand
        const cost = parseFloat(selectedBrand.cost);
        
        console.log('Calculating cost for:', {
            ingredientName: ingredient.name,
            amount,
            unit: ingredient.unit,
            cost,
            selectedBrand
        });

        return cost;
    };

    const calculateRecipeCost = async (recipe) => {
        try {
            const adjustedIngredients = recipe.ingredients.map(ing => ({
                ...ing,
                amount: (ing.amount * budgetPlan.servingSize) / recipe.servings
            }));

            const response = await axios.post(`${API_BASE_URL}/recipes/${recipe._id}/calculate-cost`, {
                ingredients: adjustedIngredients
            });
            
            console.log('API Response:', response.data);

            // Store the ingredient prices
            setIngredientPrices(prev => ({
                ...prev,
                [recipe._id]: response.data.ingredientPrices
            }));

            // Use the totalCost directly from the API response
            return parseFloat(response.data.totalCost);
        } catch (error) {
            console.error("Error calculating recipe cost:", error);
            return 0;
        }
    };

    const generateMealPlan = async () => {
        const totalMeals = calculateMealsNeeded();
        const budgetPerMeal = (budgetPlan.budget / totalMeals).toFixed(2);
        
        try {
            setLoading(true);
            setError(null);
            
            const response = await axios.get(`${API_BASE_URL}/recipes/search`, {
                params: {
                    maxPrice: budgetPerMeal,
                    isHealthy: budgetPlan.preferences.healthy,
                    isCostEffective: budgetPlan.preferences.costEffective,
                    limit: totalMeals,
                    servingSize: budgetPlan.servingSize
                },
                withCredentials: true
            });

            // Calculate costs for each recipe with serving size adjustment
            const recipesWithCosts = await Promise.all(
                response.data.map(async (recipe) => {
                    const calculatedCost = await calculateRecipeCost(recipe);
                    return {
                        ...recipe,
                        calculatedCost: Number(calculatedCost) || 0,
                        servingSize: budgetPlan.servingSize,
                        originalServings: recipe.servings
                    };
                })
            );

            // Calculate total plan cost - ensure it's a number
            const totalPlanCost = recipesWithCosts.reduce((total, recipe) => {
                return total + (Number(recipe.calculatedCost) || 0);
            }, 0);

            // Organize meals into days
            const daysInPlan = {
                weekly: 7,
                biweekly: 14,
                monthly: 30
            }[budgetPlan.duration];

            const organizedMeals = [];
            let mealIndex = 0;

            for (let day = 0; day < daysInPlan; day++) {
                const dailyMeals = {
                    day: day + 1,
                    breakfast: recipesWithCosts[mealIndex++],
                    lunch: recipesWithCosts[mealIndex++],
                    dinner: recipesWithCosts[mealIndex++]
                };

                // Calculate daily total - ensure numbers
                const dailyTotal = Object.values(dailyMeals)
                    .filter(meal => meal && typeof meal.calculatedCost !== 'undefined')
                    .reduce((total, meal) => total + (Number(meal.calculatedCost) || 0), 0);

                dailyMeals.dailyTotal = dailyTotal;
                organizedMeals.push(dailyMeals);
            }

            setMealPlan({
                meals: organizedMeals,
                summary: {
                    totalBudget: Number(budgetPlan.budget),
                    budgetPerMeal: Number(budgetPerMeal),
                    totalMeals,
                    daysInPlan,
                    servingSize: budgetPlan.servingSize,
                    totalPlanCost: Number(totalPlanCost),
                    averageDailyCost: Number((totalPlanCost / daysInPlan).toFixed(2))
                }
            });

        } catch (err) {
            setError('Failed to generate meal plan. Please try again.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const renderIngredientDetails = (recipe) => {
        if (!ingredientPrices[recipe._id]) return null;

        let totalIngredientsCost = 0;

        return (
            <div className="ingredient-details">
                <h4>Ingredients Cost Breakdown:</h4>
                {recipe.ingredients.map((ingredient, idx) => {
                    const priceData = ingredientPrices[recipe._id][ingredient.name];
                    if (!priceData) return null;

                    const selectedBrand = priceData.availableBrands.find(
                        b => b.id === priceData.selectedBrand.id
                    );

                    if (!selectedBrand) return null;

                    const adjustedAmount = (
                        (ingredient.amount * budgetPlan.servingSize) / recipe.servings
                    ).toFixed(2);

                    const ingredientCost = calculateIngredientCost(
                        { ...ingredient, amount: adjustedAmount },
                        selectedBrand
                    );
                    totalIngredientsCost += ingredientCost;

                    return (
                        <div key={idx} className="ingredient-item">
                            <div className="ingredient-info">
                                <span className="ingredient-name">{ingredient.name}</span>
                                <span className="ingredient-amount">
                                    {adjustedAmount} {ingredient.unit}
                                </span>
                            </div>
                            <div className="price-detail">
                                <div className="brand-info">
                                    {selectedBrand.brand}
                                </div>
                                <div className="cost-info">
                                    <span className="unit-cost">
                                        R{selectedBrand.costPerUnit}/{selectedBrand.baseUnit}
                                    </span>
                                    <span className="total-cost">
                                        Total: R{ingredientCost.toFixed(2)}
                                    </span>
                                    {selectedBrand.id === priceData.selectedBrand.id && 
                                        <span className="best-value"> (Best Value)</span>
                                    }
                                </div>
                            </div>
                        </div>
                    );
                })}
                <div className="ingredient-total">
                    Total Ingredients Cost: R{totalIngredientsCost.toFixed(2)}
                </div>
            </div>
        );
    };

    const handleServingSizeChange = (newServingSize) => {
        setBudgetPlan(prev => ({
            ...prev,
            servingSize: newServingSize
        }));
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        
        if (type === 'checkbox') {
            setBudgetPlan(prev => ({
                ...prev,
                preferences: {
                    ...prev.preferences,
                    [name]: checked
                }
            }));
        } else {
            setBudgetPlan(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        await generateMealPlan();
    };

    return (
        <div className="meal-budget-planner">
            <h2>Meal Budget Planner</h2>
            
            <form onSubmit={handleSubmit}>
                <div className="form-group">
                    <label>Budget Duration:</label>
                    <select 
                        name="duration" 
                        value={budgetPlan.duration}
                        onChange={handleInputChange}
                    >
                        <option value="weekly">Weekly</option>
                        <option value="biweekly">Bi-weekly</option>
                        <option value="monthly">Monthly</option>
                    </select>
                </div>

                <div className="form-group">
                    <label>Plan Type:</label>
                    <select 
                        name="planType" 
                        value={budgetPlan.planType}
                        onChange={handleInputChange}
                    >
                        <option value="single">Single Person</option>
                        <option value="family">Family Plan</option>
                    </select>
                </div>

                {budgetPlan.planType === 'family' && (
                    <div className="form-group">
                        <label>Family Size:</label>
                        <input 
                            type="number" 
                            name="familySize"
                            min="2"
                            value={budgetPlan.familySize}
                            onChange={handleInputChange}
                        />
                    </div>
                )}

                <div className="form-group">
                    <label>Budget Amount ($):</label>
                    <input 
                        type="number" 
                        name="budget"
                        value={budgetPlan.budget}
                        onChange={handleInputChange}
                        min="0"
                        step="0.01"
                    />
                </div>

                <div className="form-group">
                    <label>Preferences:</label>
                    <div className="checkbox-group">
                        <label>
                            <input 
                                type="checkbox"
                                name="costEffective"
                                checked={budgetPlan.preferences.costEffective}
                                onChange={handleInputChange}
                            />
                            Cost-Effective Meals
                        </label>
                        <label>
                            <input 
                                type="checkbox"
                                name="healthy"
                                checked={budgetPlan.preferences.healthy}
                                onChange={handleInputChange}
                            />
                            Healthy Options
                        </label>
                    </div>
                </div>

                <div className="form-group">
                    <label>Meals per Day:</label>
                    <select 
                        name="mealsPerDay" 
                        value={budgetPlan.mealsPerDay}
                        onChange={handleInputChange}
                    >
                        <option value="2">2 (Lunch & Dinner)</option>
                        <option value="3">3 (Breakfast, Lunch & Dinner)</option>
                    </select>
                </div>

                <ServingSizeSelector 
                    servingSize={budgetPlan.servingSize}
                    onChange={handleServingSizeChange}
                />

                <button type="submit" className="submit-button" disabled={loading}>
                    {loading ? 'Generating Plan...' : 'Generate Meal Plan'}
                </button>
            </form>

            {error && (
                <div className="error-message">
                    {error}
                </div>
            )}

            {mealPlan && (
                <div className="meal-plan-results">
                    <h3>Your Meal Plan</h3>
                    
                    <div className="plan-summary">
                        <p>Total Budget: R{mealPlan.summary.totalBudget}</p>
                        <p>Total Plan Cost: R{mealPlan.summary.totalPlanCost.toFixed(2)}</p>
                        <p>Average Daily Cost: R{mealPlan.summary.averageDailyCost}</p>
                        <p>Budget per Meal: R{mealPlan.summary.budgetPerMeal}</p>
                        <p>Servings per Meal: {mealPlan.summary.servingSize}</p>
                        <p>Duration: {budgetPlan.duration}</p>
                    </div>

                    <div className="meal-schedule">
                        {mealPlan.meals.map((day, index) => (
                            <div key={index} className="day-plan">
                                <h4>Day {day.day}</h4>
                                <p className="daily-total">Daily Total: R{day.dailyTotal.toFixed(2)}</p>
                                {day.breakfast && (
                                    <div className="meal-item">
                                        <h5>Breakfast</h5>
                                        <p>{day.breakfast.name}</p>
                                        <p className="price">
                                            Estimated Cost: R{typeof day.breakfast.calculatedCost === 'number' 
                                                ? day.breakfast.calculatedCost.toFixed(2) 
                                                : 'N/A'}
                                        </p>
                                        {renderIngredientDetails(day.breakfast)}
                                    </div>
                                )}
                                {day.lunch && (
                                    <div className="meal-item">
                                        <h5>Lunch</h5>
                                        <p>{day.lunch.name}</p>
                                        <p className="price">
                                            Estimated Cost: R{typeof day.lunch.calculatedCost === 'number'
                                                ? day.lunch.calculatedCost.toFixed(2)
                                                : 'N/A'}
                                        </p>
                                        {renderIngredientDetails(day.lunch)}
                                    </div>
                                )}
                                {day.dinner && (
                                    <div className="meal-item">
                                        <h5>Dinner</h5>
                                        <p>{day.dinner.name}</p>
                                        <p className="price">
                                            Estimated Cost: R{typeof day.dinner.calculatedCost === 'number'
                                                ? day.dinner.calculatedCost.toFixed(2)
                                                : 'N/A'}
                                        </p>
                                        {renderIngredientDetails(day.dinner)}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default MealBudgetPlanner; 